import { CellStyle, ValueFormatterParams } from 'ag-grid-enterprise'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)

export function coloredNumberCellStyle(params: { value: number }): CellStyle {
  if (params.value > 0)
    return { color: `var(--ag-grid-table-green)`, textAlign: 'right' }
  if (params.value < 0)
    return { color: `var(--ag-grid-table-red)`, textAlign: 'right' }
  return { textAlign: 'right' } as CellStyle
}

export function numberCellStyle(): CellStyle {
  return { textAlign: 'right' } as CellStyle
}

export function percentageValueFormatter(
  params: ValueFormatterParams,
  options: { decimal?: number } = {}
) {
  const { value } = params
  const decimal = options.decimal ?? 2
  if (typeof value !== 'number' || isNaN(value)) return ''
  return `${(value * 100).toFixed(decimal)}%`
}

export function currencyValueFormatter(
  params: ValueFormatterParams,
  options: { decimal?: number; currency?: string } = {}
): string {
  const { value, data } = params
  // Prefer options.currency, fallback to params.data?.tickerCurrency
  const currency = options.currency || data?.tickerCurrency || ''
  const decimal = options.decimal ?? 2

  if (value === undefined || value === null || value === '') {
    return ''
  }

  const num = typeof value === 'string' ? parseFloat(value) : value
  if (typeof num !== 'number' || isNaN(num)) {
    return ''
  }

  if (currency) {
    const absNum = Math.abs(num)
    const formattedNumber = absNum.toLocaleString('en-US', {
      minimumFractionDigits: decimal,
      maximumFractionDigits: decimal,
    })

    let currencySymbol
    // Special case for japanese currency
    if (currency.toUpperCase() === 'JPY') {
      currencySymbol = 'JP¥'
    } else if (currency.toUpperCase() === 'SGD') {
      currencySymbol = 'S$'
    } else {
      currencySymbol =
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency,
        })
          .formatToParts(1)
          .find((part) => part.type === 'currency')?.value || currency
    }

    if (num < 0) {
      return `(${currencySymbol} ${formattedNumber})`
    } else {
      return `${currencySymbol} ${formattedNumber}`
    }
  }

  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimal,
    maximumFractionDigits: decimal,
  })
}

export function formatCurrency(
  value: number,
  options: { decimal?: number; currency?: string } = {}
): string {
  const mockParams = { value, data: {} } as ValueFormatterParams
  return currencyValueFormatter(mockParams, options)
}

export function marketDateTimezoneFormatter(
  params: {
    value: string | null | undefined
    data: { marketTimeZone: string }
  },
  options: {
    showTime?: boolean
    showTimezone?: boolean
    dateOnly?: boolean
  } = {
    showTime: true,
    showTimezone: false,
    dateOnly: false,
  }
) {
  if (!params.value) {
    return ''
  }

  const showTime = options.showTime ?? true

  const getTimezoneAbbreviation = (timezone: string, date: dayjs.Dayjs) => {
    // Check if DST by comparing UTC offset in January vs July
    const januaryOffset = dayjs().tz(timezone).month(0).utcOffset()
    const julyOffset = dayjs().tz(timezone).month(6).utcOffset()
    const currentOffset = date.utcOffset()

    // If offsets are different, timezone observes DST
    // Current date is in DST if its offset matches the July offset (for northern hemisphere)
    const isDST = januaryOffset !== julyOffset && currentOffset === julyOffset

    const timezoneMap: { [key: string]: { standard: string; dst: string } } = {
      'Asia/Hong_Kong': { standard: 'HKT', dst: 'HKT' },
      'Asia/Shanghai': { standard: 'CST', dst: 'CST' },
      'Asia/Tokyo': { standard: 'JST', dst: 'JST' },
      'America/New_York': { standard: 'EST', dst: 'EDT' },
      'Europe/London': { standard: 'GMT', dst: 'BST' },
      'Asia/Singapore': { standard: 'SGT', dst: 'SGT' },
      'Australia/Sydney': { standard: 'AEST', dst: 'AEDT' },
      'Asia/Seoul': { standard: 'KST', dst: 'KST' },
      'Asia/Taipei': { standard: 'CST', dst: 'CST' },
      'Asia/Kolkata': { standard: 'IST', dst: 'IST' },
    }

    const mapping = timezoneMap[timezone]
    if (!mapping) return timezone

    return isDST ? mapping.dst : mapping.standard
  }

  const dayjsObj = dayjs(params.value).tz(params.data.marketTimeZone)

  if (options.dateOnly) {
    return dayjsObj.format('YYYY-MM-DD')
  }

  const abbreviation = getTimezoneAbbreviation(
    params.data.marketTimeZone,
    dayjsObj
  )

  if (options.showTimezone) {
    return dayjsObj.format(`YYYY-MM-DD HH:mm`) + ' ' + abbreviation
  }

  if (!showTime) {
    return dayjsObj.format(`YYYY-MM-DD`)
  }
  return dayjsObj.format(`YYYY-MM-DD HH:mm`)
}

function getDecimalPlaces(num: number) {
  if (Number.isInteger(num)) {
    return 0 // No decimal places for integers
  }
  const decimalPart = num.toString().split('.')[1]
  return decimalPart ? decimalPart.length : 0
}

export function millionFormatter(
  params: { value: number },
  options: { decimal?: number; includeSymbol?: boolean } = {}
): string {
  if (!params.value) return ''
  const value = params.value / 1000000
  let decimal = getDecimalPlaces(value)
  const { decimal: optDecimal = 2, includeSymbol = true } = options
  if (decimal > optDecimal) {
    decimal = optDecimal
  }

  if (includeSymbol) {
    return `$ ${value.toFixed(decimal)}M`
  } else {
    return `${value.toFixed(decimal)}M`
  }
}

export function capitalizeFirstLetter(str: string) {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}
